import React from "react";
import { DEFAULT_CONTROL_CLASS, DISABLED_CLASS } from "../constants";
import { useFilingFolderOptions } from "../../../../hooks/edms/useFilingFolderOptions";
import {
  isFilingStatus,
  shouldShowFilingGroupSelectors,
} from "../../../../hooks/workflow/filingSectionVisibility";

/**
 * File Type + File Name section: two select fields in a grid.
 * Uses workflow control classes for consistency with other workflow form fields.
 * Visibility controlled by canShowFileTypeFileNameSection (role-based).
 *
 * @param {Object} props
 * @param {{ fileType: string, fileName: string }} props.value
 * @param {function({ fileType: string, fileName: string }): void} props.onChange
 * @param {Array<{ value: string, label: string }>} [props.fileTypeOptions]
 * @param {Array<{ value: string, label: string }>} [props.fileNameOptions]
 * @param {boolean} [props.disabled]
 * @param {string} [props.errorFileType]
 * @param {string} [props.errorFileName]
 * @param {number} [props.labelWidthRem]
 * @param {'left'|'top'} [props.labelPosition]
 */
export function FileTypeFileNameSection({
  value = {},
  onChange,
  fileTypeOptions = [],
  fileNameOptions = [],
  disabled = false,
  errorFileType,
  errorFileName,
  labelWidthRem = 6,
  labelPosition = "left",
  workflowStatus,
}) {
  const { fileType = "", fileName = "", groupCategory = "", groupSubCategory = "" } = value;

  const isFiling = isFilingStatus(workflowStatus);
  const showGroupSelectors = shouldShowFilingGroupSelectors(workflowStatus, value);

  const ensureSelectedOption = (options, selectedValue) => {
    const selected = String(selectedValue ?? "");
    if (!selected) return options ?? [];
    const arr = Array.isArray(options) ? options : [];
    const has = arr.some((o) => String(o?.value) === selected);
    if (has) return arr;
    return [...arr, { value: selected, label: selected }];
  };

  const {
    groupCategoryOptions,
    groupSubCategoryOptions,
    folderOptions: filingFolderOptions,
  } = useFilingFolderOptions({
    groupCategory: groupCategory || undefined,
    groupSubCategory: groupSubCategory || undefined,
    // folderTypeId is optional for /by-group-and-type; folder type selection removed from UI
    folderTypeId: fileType || undefined,
    enabled: showGroupSelectors,
  });

  const optionsBase = Array.isArray(filingFolderOptions) && filingFolderOptions.length > 0 ? filingFolderOptions : (fileNameOptions ?? []);
  const effectiveFileNameOptions = ensureSelectedOption(optionsBase, fileName);
  const isTop = labelPosition === "top";
  const gridStyle = isTop
    ? undefined
    : { gridTemplateColumns: `${labelWidthRem}rem 1fr` };

  const controlClass = disabled ? DISABLED_CLASS : DEFAULT_CONTROL_CLASS;

  const handleFileNameChange = (e) => {
    const next = e.target.value ?? "";
    if (!isFiling) {
      onChange({ ...value, fileName: next });
      return;
    }

    const selected = (filingFolderOptions || []).find((opt) => String(opt.value) === String(next));
    const nextFolderTypeId = selected?.folderTypeId ?? fileType ?? "";
    onChange({
      ...value,
      fileName: next,
      fileType: String(nextFolderTypeId || ""),
    });
  };

  const handleGroupCategoryChange = (e) => {
    const next = e.target.value ?? "";
    onChange({
      ...value,
      groupCategory: next,
      groupSubCategory: "",
      fileName: "",
      fileType: "",
    });
  };

  const handleGroupSubCategoryChange = (e) => {
    const next = e.target.value ?? "";
    onChange({
      ...value,
      groupSubCategory: next,
      fileName: "",
      fileType: "",
    });
  };

  const selectEl = (label, id, val, options, error, handleChange) => (
    <div
      className={isTop ? "space-y-1" : "grid items-center gap-y-1"}
      style={isTop ? undefined : gridStyle}
    >
      <label htmlFor={id} className="text-sm font-medium text-primary-dark">
        {label}
      </label>
      <div className="min-w-0">
        <select
          id={id}
          value={val}
          onChange={handleChange}
          disabled={disabled}
          className={controlClass}
          aria-invalid={error ? true : undefined}
          aria-describedby={error && id ? `${id}-error` : undefined}
        >
          <option value="">Select...</option>
          {(options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && <p id={id ? `${id}-error` : undefined} className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-2 w-full">
      {showGroupSelectors && (
        <>
          {selectEl(
            "Group:",
            "groupCategory",
            groupCategory,
            ensureSelectedOption(groupCategoryOptions, groupCategory),
            null,
            handleGroupCategoryChange,
          )}
          {String(groupCategory) === "Personal" &&
            selectEl(
              "Sub Group:",
              "groupSubCategory",
              groupSubCategory,
              ensureSelectedOption(groupSubCategoryOptions, groupSubCategory),
              null,
              handleGroupSubCategoryChange,
            )}
        </>
      )}
      {selectEl(
        "File:",
        "fileName",
        fileName,
        effectiveFileNameOptions,
        errorFileName,
        handleFileNameChange,
      )}
    </div>
  );
}

export default FileTypeFileNameSection;
